import { useTranslation } from 'react-i18next';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01Icon, GlobeIcon } from '@hugeicons/core-free-icons';
import { Button } from '@components/Button/Button';
import { useLanguage } from '@hooks/useLanguage';
import { RtlBackIcon } from '@utils/rtlIcons';

export type AuthFlowPageId =
  | 'signup'
  | 'signup-verify'
  | 'signup-complete'
  | 'nafath-verification'
  | 'select-national-address'
  | 'success';

export interface AuthFlowHeaderProps {
  currentPage: AuthFlowPageId;
  onBack?: () => void;
}

export function AuthFlowHeader({ currentPage, onBack }: AuthFlowHeaderProps) {
  const { t } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const showBack = currentPage !== 'signup' && !!onBack;
  const languageLabel = language === 'ar' ? t('common.english') : t('common.arabic');

  return (
    <header
      className="absolute top-0 start-0 end-0 z-10 flex w-full items-center justify-between gap-2 ps-6 pe-6 py-4 sm:ps-10 sm:pe-10 md:ps-12 md:pe-12 lg:ps-16 lg:pe-16 font-sans bg-surface-web"
      aria-label="Auth navigation"
    >
      {showBack ? (
        <Button
          variant="ghost"
          size="md"
          className="h-14 p-4 rounded-[16px]"
          onClick={onBack}
          icon={<RtlBackIcon><HugeiconsIcon icon={ArrowLeft01Icon} size={20} /></RtlBackIcon>}
          iconPosition="left"
        >
          {t('common.back')}
        </Button>
      ) : (
        <span />
      )}
      <Button
        variant="ghost"
        size="md"
        className="h-14 p-4 rounded-[16px] shrink-0"
        icon={<HugeiconsIcon icon={GlobeIcon} size={20} />}
        iconPosition="right"
        aria-label={languageLabel}
        onClick={toggleLanguage}
      >
        {languageLabel}
      </Button>
    </header>
  );
}

export function AuthFlowFooter() {
  const { t } = useTranslation();
  return (
    <footer
      className="absolute bottom-0 start-0 end-0 z-10 py-4 text-center font-sans text-base font-normal leading-snug text-primary/70 bg-surface-web"
      aria-label="Copyright"
    >
      {t('auth.footer')}
    </footer>
  );
}

export interface AuthFlowShellProps {
  /** Header: Back + Language (positioned at top of left column) */
  header: React.ReactNode;
  /** Footer: Copyright (positioned at bottom of left column) */
  footer: React.ReactNode;
  /** Center content (animated); vertically centered between header and footer */
  children: React.ReactNode;
  /** Right panel: hero image (fixed, fills container) */
  heroImage: string;
}

/** Layout per design: margin around entire layout + gutter between left and hero. 50/50 split inside padded area. */
export function AuthFlowShell({ header, footer, children, heroImage }: AuthFlowShellProps) {
  return (
    <div className="min-h-screen w-full bg-surface-web font-sans px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
      <div className="mx-auto grid min-h-[calc(100vh-2rem)] sm:min-h-[calc(100vh-3rem)] lg:min-h-[calc(100vh-4rem)] w-full max-w-7xl grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr] lg:gap-6">
        {/* Left: content area with margin from outer padding */}
        <div className="relative flex min-h-[calc(100vh-2rem)] min-w-0 flex-col overflow-visible rounded-2xl bg-surface-web sm:min-h-[calc(100vh-3rem)] lg:min-h-[calc(100vh-4rem)]">
          {header}
          <main className="flex flex-1 flex-col items-center justify-center overflow-visible px-6 pt-20 pb-16 sm:px-8 md:px-10 lg:px-12">
            {children}
          </main>
          {footer}
        </div>
        {/* Right: hero panel with margin (rounded card), gutter from left */}
        <div
          className="hidden min-h-0 min-w-0 lg:block"
          aria-hidden
        >
          <div className="h-full min-h-[520px] w-full overflow-hidden rounded-2xl">
            <img
              src={heroImage}
              alt=""
              width={646}
              height={520}
              loading="lazy"
              decoding="async"
              className="block h-full min-h-[520px] w-full object-cover object-center select-none pointer-events-none"
              aria-hidden
            />
          </div>
        </div>
      </div>
    </div>
  );
}
